import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Grid,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageLayout from '../../components/layout/PageLayout';
import api from '../../api/config';
import { SERVICES, STATUSES } from '../../api/endpoints';
import { extractList } from '../../utils/response';
import { toast } from 'react-toastify';
import { buildServiceRelationParams } from './serviceApiHelpers';

const buildFormData = (values) => {
  const fd = new FormData();
  fd.append('status_id', values.status_id);
  fd.append('name', values.name ?? '');
  fd.append('internal_charges', String(values.internal_charges ?? ''));
  fd.append('external_charges', String(values.external_charges ?? ''));
  fd.append('description', values.description ?? '');
  return fd;
};

const statusLabel = (s) => s.label ?? s.name ?? s.title ?? String(s.id);

const CreateService = () => {
  const { id: editId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(editId);

  const [loadingPage, setLoadingPage] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);

  const [statusOptions, setStatusOptions] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [name, setName] = useState('');
  const [internalCharges, setInternalCharges] = useState('');
  const [externalCharges, setExternalCharges] = useState('');
  const [description, setDescription] = useState('');

  const [serviceRecord, setServiceRecord] = useState(null);

  const fetchStatuses = useCallback(async () => {
    setStatusLoading(true);
    try {
      let response;
      try {
        response = await api.get(STATUSES.BY_MODEL('service'));
      } catch {
        response = await api.get(STATUSES.LIST, { params: { paginate: 0 } });
      }
      const list = extractList(response.data);
      const arr = Array.isArray(list) ? list : [];
      setStatusOptions(
        arr.map((s) => ({
          id: s.id,
          label: statusLabel(s),
          raw: s,
        }))
      );
    } catch {
      toast.error('Could not load statuses');
      setStatusOptions([]);
    } finally {
      setStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  useEffect(() => {
    if (!isEdit || !editId) {
      setLoadingPage(false);
      setServiceRecord(null);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoadingPage(true);
      try {
        const { data } = await api.get(SERVICES.BY_ID(editId), {
          params: buildServiceRelationParams(),
        });
        const s = data?.data ?? data;
        if (cancelled || !s) return;
        setServiceRecord(s);
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to load service');
        navigate('/services');
      } finally {
        if (!cancelled) setLoadingPage(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isEdit, editId, navigate]);

  useEffect(() => {
    if (!isEdit || !serviceRecord) return;
    const s = serviceRecord;

    setName(s.name ?? '');
    setInternalCharges(s.internal_charges != null ? String(s.internal_charges) : '');
    setExternalCharges(s.external_charges != null ? String(s.external_charges) : '');
    setDescription(s.description ?? '');

    const nestedStatus = s.status && typeof s.status === 'object' ? s.status : null;
    const sid = s.status_id ?? nestedStatus?.id ?? undefined;

    if (sid) {
      if (nestedStatus) {
        const opt = { id: sid, label: statusLabel(nestedStatus), raw: nestedStatus };
        setSelectedStatus(opt);
        setStatusOptions((prev) => (prev.some((o) => o.id === sid) ? prev : [...prev, opt]));
      } else {
        const opt = statusOptions.find((o) => o.id === sid);
        setSelectedStatus(opt ?? { id: sid, label: String(sid), raw: {} });
      }
    } else {
      setSelectedStatus(null);
    }
  }, [isEdit, serviceRecord, statusOptions]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStatus?.id || !name.trim()) {
      toast.warn('Select status and enter a service name');
      return;
    }
    if (
      internalCharges === '' ||
      externalCharges === '' ||
      Number.isNaN(Number(internalCharges)) ||
      Number.isNaN(Number(externalCharges))
    ) {
      toast.warn('Enter valid internal and external charges');
      return;
    }

    const values = {
      status_id: selectedStatus.id,
      name: name.trim(),
      internal_charges: internalCharges,
      external_charges: externalCharges,
      description: description.trim(),
    };
    const fd = buildFormData(values);
    const q = new URLSearchParams(buildServiceRelationParams()).toString();

    setSubmitting(true);
    try {
      if (isEdit) {
        await api.put(`${SERVICES.BY_ID(editId)}?${q}`, fd);
        toast.success('Service updated');
      } else {
        await api.post(SERVICES.LIST, fd);
        toast.success('Service created');
      }
      navigate('/services');
    } catch (err) {
      toast.error(
        err.response?.data?.message || (isEdit ? 'Failed to update service' : 'Failed to create service')
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingPage) {
    return (
      <PageLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/services')} sx={{ mb: 2 }}>
          Back to services
        </Button>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
          {isEdit ? 'Edit service' : 'Create service'}
        </Typography>

        <Card>
          <CardContent component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={statusOptions}
                  value={selectedStatus}
                  onChange={(_, v) => setSelectedStatus(v)}
                  loading={statusLoading}
                  getOptionLabel={(opt) => (opt?.label != null ? opt.label : '')}
                  isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  renderInput={(params) => <TextField {...params} label="Status" required fullWidth />}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Internal charges"
                  value={internalCharges}
                  onChange={(e) => setInternalCharges(e.target.value)}
                  required
                  fullWidth
                  type="number"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="External charges"
                  value={externalCharges}
                  onChange={(e) => setExternalCharges(e.target.value)}
                  required
                  fullWidth
                  type="number"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  fullWidth
                  multiline
                  minRows={3}
                />
              </Grid>
              <Grid item xs={12} sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Button type="submit" variant="contained" disabled={submitting}>
                  {submitting ? <CircularProgress size={22} /> : isEdit ? 'Save changes' : 'Create service'}
                </Button>
                <Button type="button" variant="outlined" onClick={() => navigate('/services')} disabled={submitting}>
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default CreateService;
